import { useEffect, useState } from "react";
import { Pause, Play, SkipBack, SkipForward, X } from "lucide-react";
import { useMusic } from "@/lib/music/MusicProvider";
import { clock, useCountdown } from "@/components/music/SprintTab";
import { FavButton } from "@/components/music/FavButton";
import { Cover } from "@/components/music/Cover";
import { AddToMenu } from "@/components/music/AddToMenu";

const fmt = (s) => (Number.isFinite(s) ? `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}` : "0:00");

export const useProgress = (audio) => {
  const [t, setT] = useState({ cur: 0, dur: 0 });
  useEffect(() => {
    const a = audio.current;
    if (!a) return undefined;
    const u = () => setT({ cur: a.currentTime, dur: a.duration || 0 });
    a.addEventListener("timeupdate", u); a.addEventListener("loadedmetadata", u);
    return () => { a.removeEventListener("timeupdate", u); a.removeEventListener("loadedmetadata", u); };
  }, [audio]);
  return t;
};

export const Seek = ({ audio, testid }) => {
  const { cur, dur } = useProgress(audio);
  return (
    <div className="flex items-center gap-2 text-[10px] tabular-nums text-[#7A7468]">
      <span>{fmt(cur)}</span>
      <input type="range" min={0} max={dur || 1} step={0.5} value={cur} onChange={(e) => { audio.current.currentTime = Number(e.target.value); }} className="h-1 flex-1 cursor-pointer accent-[#F59E0B]" aria-label="Seek" data-testid={testid} />
      <span>{fmt(dur)}</span>
    </div>
  );
};

const SprintChip = ({ m }) => {
  const left = useCountdown(m.sprint);
  return <span className="shrink-0 rounded-full bg-[#D97706]/15 px-2 py-0.5 font-mono text-[11px] tabular-nums text-[#FBBF24]" title="Sprint time left" data-testid="music-mini-sprint">{clock(left)}</span>;
};

export const MiniBar = () => {
  const m = useMusic();
  if (!m?.current && !m?.sprint) return null;
  const Btn = ({ onClick, label, children, testid }) => <button onClick={onClick} title={label} aria-label={label} className="rounded-full p-1.5 text-[#D6CCBC] transition-colors hover:bg-[#1A202C] hover:text-[#F3EBDD]" data-testid={testid}>{children}</button>;
  return (
    <div className="fixed bottom-4 right-4 z-40 hidden w-[340px] rounded-lg border border-[#2D3442] bg-[#0F131A]/95 p-3 shadow-2xl backdrop-blur-md animate-in slide-in-from-bottom-3 fade-in duration-300 lg:block" data-testid="music-mini-bar">
      <div className="flex items-center gap-2">
        <Cover src={m.current ? m.cover : null} size="h-10 w-10" pulse={m.playing} testid="music-mini-cover" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm text-[#F3EBDD]" data-testid="music-mini-title">{m.current ? m.current.title : "Writing sprint"}</div>
          <div className="truncate text-[11px] text-[#7A7468]">{m.current ? [m.current.artist, m.current.album, m.q.label !== m.current.album && m.q.label].filter(Boolean).join(" · ") : "No music"}</div>
        </div>
        {m.sprint && <SprintChip m={m} />}
        <Btn onClick={m.prev} label="Previous" testid="music-mini-prev"><SkipBack className="h-3.5 w-3.5" /></Btn>
        <Btn onClick={m.toggle} label={m.playing ? "Pause" : "Play"} testid="music-mini-toggle">{m.playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}</Btn>
        <Btn onClick={m.next} label="Next" testid="music-mini-next"><SkipForward className="h-3.5 w-3.5" /></Btn>
        <Btn onClick={m.stop} label="Stop and close" testid="music-mini-close"><X className="h-3.5 w-3.5" /></Btn>
      </div>
      {m.blocked && <button onClick={m.toggle} className="mt-2 w-full rounded bg-[#D97706] py-1 text-xs font-medium text-[#0B0D10]" data-testid="music-mini-start">Play {m.q.label || "music"}</button>}
      {m.current && <div className="mt-2 flex items-center gap-1">
        <div className="min-w-0 flex-1"><Seek audio={m.audio} testid="music-mini-seek" /></div>
        <span className="px-1"><FavButton m={m} testid="music-mini-fav" /></span>
        <AddToMenu m={m} path={m.current.path} testid="music-mini-add" />
      </div>}
    </div>
  );
};
